import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  // FormControlLabel, // Clarification phase disabled for now, might need in future
  // Checkbox, // Clarification phase disabled for now, might need in future
} from '@mui/material';
import { Send, Info } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { clearError } from '../../store/strategistSlice';
import { createBreakdown } from '../../store/strategistSlice';
import { AGENT_OUTPUT_LEVELS } from '../../shared/appConstants';
import { toast } from 'react-toastify';

const AgentCommandBar = ({ projectId, initialRequirement, outputLevel, isGenerating, onBreakdownCreated, onClearRequested }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { error } = useSelector((state) => state.strategist);
  const { loading } = useSelector((state) => state.strategist);

  const [text, setText] = useState('');
  const [selectedOutputLevel, setSelectedOutputLevel] = useState(AGENT_OUTPUT_LEVELS.EPIC);
  const [prevProjectId, setPrevProjectId] = useState(projectId);

  // Expose clear function to parent
  React.useImperativeHandle(onClearRequested, () => ({
    clearFields: () => {
      setText('');
      setSelectedOutputLevel(AGENT_OUTPUT_LEVELS.EPIC);
    }
  }));

  // Update state when props change
  React.useEffect(() => {
    if (initialRequirement) {
      setText(initialRequirement);
    } else {
      setText(''); // Clear text when no initial requirement
    }
    if (outputLevel) {
      setSelectedOutputLevel(outputLevel);
    } else {
      setSelectedOutputLevel(AGENT_OUTPUT_LEVELS.EPIC); // Reset to default
    }
  }, [initialRequirement, outputLevel]);

  // Clear state when projectId changes
  React.useEffect(() => {
    if (projectId !== prevProjectId) {
      setText('');
      setSelectedOutputLevel(AGENT_OUTPUT_LEVELS.EPIC);
      setPrevProjectId(projectId);
    }
  }, [projectId, prevProjectId]);

  const handleProcess = async () => {
    if (!text.trim() || !projectId) {
      return;
    }
    
    try {
      await dispatch(createBreakdown({
        projectId,
        requirement: text.trim(),
        customHierarchy: selectedOutputLevel
      })).unwrap();
      
      toast.info(t('agent.commandBar.generatingBreakdown'));
      
      // Call the callback to fetch breakdown status after successful creation
      if (onBreakdownCreated) {
        onBreakdownCreated();
      }
    } catch (err) {
      toast.error(err || t('agent.commandBar.breakdownFailed'));
    }
  };

  const hasRequirement = initialRequirement && initialRequirement.trim().length > 0;
  const showProcessButton = !hasRequirement && !isGenerating;

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleProcess();
    }
  };

  const getBreakdownInfo = () => {
    const message = t('agent.commandBar.breakdownInfo', { 
      level: t(`agent.breakdown.${outputLevel}`)
    });
    // Split message and render with bold parts
    const parts = message.split(/(<strong>.*?<\/strong>)/g);
    return (
      <>
        {parts.map((part, index) => {
          if (part.startsWith('<strong>') && part.endsWith('</strong>')) {
            const boldText = part.replace(/<\/?strong>/g, '');
            return <strong key={index}>{boldText}</strong>;
          }
          return <span key={index}>{part}</span>;
        })}
      </>
    );
  };

  return (
    <Box className="agent-command-bar-container">
      <Box className="command-bar-header">
        <Typography variant="h6" className="command-bar-title">
          {hasRequirement ? t('agent.commandBar.requirement') : t('agent.commandBar.title')}
        </Typography>
        <FormControl className="command-bar-form-control" sx={{ minWidth: 200 }}>
          <InputLabel>{t('agent.commandBar.outputLevel')}</InputLabel>
          <Select
            value={selectedOutputLevel}
            onChange={(e) => setSelectedOutputLevel(e.target.value)}
            label={t('agent.commandBar.outputLevel')}
            disabled={hasRequirement || isGenerating}
          >
            <MenuItem value={AGENT_OUTPUT_LEVELS.EPIC}>{t('agent.breakdown.epic')}</MenuItem>
            <MenuItem value={AGENT_OUTPUT_LEVELS.FEATURE}>{t('agent.breakdown.feature')}</MenuItem>
            <MenuItem value={AGENT_OUTPUT_LEVELS.TASK}>{t('agent.breakdown.task')}</MenuItem>
            <MenuItem value={AGENT_OUTPUT_LEVELS.SUBTASK}>{t('agent.breakdown.subtask')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" className="command-bar-error" onClose={() => dispatch(clearError())}>
          {error.message || error}
        </Alert>
      )}

      {!hasRequirement && (
        <Alert 
          severity="info" 
          icon={<Info className="command-bar-info-icon" />}
          className="command-bar-info"
        >
          <Typography variant="body2" className="command-bar-info-text">
            {getBreakdownInfo()}
          </Typography>
        </Alert>
      )}

      {hasRequirement ? (
        <Box className="command-bar-requirement-display">
          <Typography variant="body1" className="requirement-text">
            {initialRequirement}
          </Typography>
        </Box>
      ) : (
        <Box className="command-bar-input-container">
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder={t('agent.commandBar.placeholder')}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading.createBreakdown}
            variant="outlined"
            className="command-bar-textfield"
          />
        </Box>
      )}

      <Box className="command-bar-actions">
        {showProcessButton && (
          <Button
            variant="contained"
            onClick={handleProcess}
            disabled={!text.trim() || !projectId || loading.createBreakdown}
            startIcon={loading.createBreakdown ? <CircularProgress size={20} /> : <Send />}
            className="command-bar-process-button"
          >
            {loading.createBreakdown ? t('agent.commandBar.processing') : t('agent.commandBar.process')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default AgentCommandBar;



